import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase.js';
import { getPreviewState, subscribePreview, type PreviewState } from './preview.js';
import { useToast } from './toast.js';

/**
 * Auth context shared by agency and portal sides. Wraps Supabase Auth.
 *
 * `kind` is derived from the JWT's user_metadata.scope_role:
 *   - 'agency' → AGENCY_USER (sees agency app)
 *   - 'contact' → CLIENT_CONTACT (sees /portal)
 *   - 'anonymous' → not signed in
 */

export type AuthKind = 'agency' | 'contact' | 'anonymous';

export interface AgencyAuth {
  kind: 'agency';
  userId: string;
  agencyId: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'CONTRIBUTOR';
  email: string;
  session: Session;
  user: User;
}

export interface ContactAuth {
  kind: 'contact';
  contactId: string;
  clientId: string;
  agencyId: string;
  role: 'CLIENT_ADMIN' | 'CLIENT_VIEWER';
  email: string;
  session: Session;
  user: User;
}

export interface AnonymousAuth {
  kind: 'anonymous';
}

export type Auth = AgencyAuth | ContactAuth | AnonymousAuth;

interface AuthContextValue {
  auth: Auth;
  loading: boolean;
  /**
   * True while the user is in a Supabase password-recovery session.
   * In that state the session is real (so updateUser can succeed) but the
   * app must NOT grant normal access — the recovery link should be exchangeable
   * only for "set a new password," not for full app entry.
   */
  recovery: boolean;
  /** Mark recovery complete (call from the reset-password screen after success). */
  clearRecovery: () => void;
  signOut: () => Promise<void>;
  /**
   * True when an agency admin is read-only previewing as a client contact.
   * `auth` is synthesized to look like that contact so portal screens work
   * unchanged. UI components can hide action buttons by checking this flag.
   */
  previewMode: boolean;
  /** The underlying agency-user identity behind the preview, if any. */
  realAgencyAuth: AgencyAuth | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Detect a fresh recovery callback. Supabase puts `type=recovery` in the URL
 * hash when the user opens the reset link; we capture that synchronously so
 * the flag is set before any guard runs.
 */
function urlHasRecoveryToken(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  return hash.includes('type=recovery');
}

const RECOVERY_FLAG_KEY = 'scope.auth.recovery';

function deriveAuth(session: Session | null): Auth {
  if (!session) return { kind: 'anonymous' };
  const metadata = session.user.user_metadata ?? {};
  const role = (metadata.scope_role ?? '') as string;
  const email = session.user.email ?? '';

  if (role === 'AGENCY_USER' && metadata.user_id && metadata.agency_id) {
    const userRole = (metadata.role ?? 'CONTRIBUTOR') as 'SUPER_ADMIN' | 'ADMIN' | 'CONTRIBUTOR';
    return {
      kind: 'agency',
      userId: metadata.user_id,
      agencyId: metadata.agency_id,
      role: userRole,
      email,
      session,
      user: session.user,
    };
  }

  if (
    role === 'CLIENT_CONTACT' &&
    metadata.contact_id &&
    metadata.client_id &&
    metadata.agency_id
  ) {
    const contactRole = (metadata.role ?? 'CLIENT_VIEWER') as 'CLIENT_ADMIN' | 'CLIENT_VIEWER';
    return {
      kind: 'contact',
      contactId: metadata.contact_id,
      clientId: metadata.client_id,
      agencyId: metadata.agency_id,
      role: contactRole,
      email,
      session,
      user: session.user,
    };
  }

  return { kind: 'anonymous' };
}

/** Synthesise a ContactAuth from the underlying agency session + preview state. */
function buildPreviewAuth(agency: AgencyAuth, preview: PreviewState): ContactAuth {
  return {
    kind: 'contact',
    contactId: preview.contactId,
    clientId: preview.clientId,
    agencyId: preview.agencyId,
    role: preview.contactRole,
    email: preview.contactEmail,
    // Re-use the agency user's actual Supabase session + user. tRPC requests
    // still go out with the agency JWT; the server swaps auth based on the
    // x-scope-preview-as-contact-id header, not the JWT.
    session: agency.session,
    user: agency.user,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [rawAuth, setRawAuth] = useState<Auth>({ kind: 'anonymous' });
  const [previewState, setPreviewState] = useState<PreviewState | null>(() => getPreviewState());
  const [loading, setLoading] = useState(true);
  // Tracks whether the most recent SIGNED_OUT was triggered by the user
  // clicking sign-out (as opposed to an expired JWT). Lets us toast only
  // for unexpected sign-outs.
  const intentionalSignOutRef = useRef(false);
  // Was there a real session in the previous tick? Needed so we don't
  // toast on the initial anonymous → anonymous transition at app boot.
  const hadSessionRef = useRef(false);
  // Seed recovery synchronously: either the URL fragment carries
  // type=recovery (fresh click of the reset link) or sessionStorage holds
  // a sticky flag from a prior tick.
  const [recovery, setRecovery] = useState<boolean>(() => {
    if (urlHasRecoveryToken()) return true;
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(RECOVERY_FLAG_KEY)) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!active) return;
      hadSessionRef.current = !!session;
      setRawAuth(deriveAuth(session));
      setLoading(false);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecovery(true);
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
        }
      }
      if (event === 'SIGNED_OUT') {
        setRecovery(false);
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(RECOVERY_FLAG_KEY);
        }
        // If the user didn't press sign-out themselves and they were
        // previously signed in, this is a token expiry. Surface it so they
        // know why they're being kicked to login.
        if (!intentionalSignOutRef.current && hadSessionRef.current) {
          toast.info('Your session has expired. Please sign in again.');
        }
        intentionalSignOutRef.current = false;
      }
      hadSessionRef.current = !!session;
      setRawAuth(deriveAuth(session));
    });
    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [toast]);

  // Keep preview state in sync across the tab — preview.start in another
  // component updates sessionStorage; we re-read here on every change so the
  // synthesized ContactAuth (below) refreshes.
  useEffect(() => {
    return subscribePreview(() => setPreviewState(getPreviewState()));
  }, []);

  const clearRecovery = () => {
    setRecovery(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(RECOVERY_FLAG_KEY);
    }
  };

  // When an agency admin is previewing, swap the auth we expose to consumers
  // to a synthesized ContactAuth so portal screens work unchanged.
  const previewActive = previewState !== null && rawAuth.kind === 'agency';
  const auth: Auth = previewActive
    ? buildPreviewAuth(rawAuth as AgencyAuth, previewState as PreviewState)
    : rawAuth;
  const realAgencyAuth: AgencyAuth | null =
    rawAuth.kind === 'agency' ? (rawAuth as AgencyAuth) : null;

  const value: AuthContextValue = {
    auth,
    loading,
    recovery,
    clearRecovery,
    signOut: async () => {
      clearRecovery();
      intentionalSignOutRef.current = true;
      await supabase.auth.signOut();
    },
    previewMode: previewActive,
    realAgencyAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience hook — true when an agency admin is previewing as a contact. */
export function useIsPreview(): boolean {
  return useAuth().previewMode;
}

/** Guard component — renders children only if auth.kind matches. */
export function RequireAuth({
  kind,
  children,
  fallback,
}: {
  kind: 'agency' | 'contact';
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { auth, loading } = useAuth();
  if (loading) return null;
  if (auth.kind !== kind) return <>{fallback ?? null}</>;
  return <>{children}</>;
}

/** Guard component — renders children only if user role meets minimum. */
export function RequireRole({
  role,
  children,
  fallback,
}: {
  role: 'SUPER_ADMIN' | 'ADMIN' | 'ADMIN+' | 'CONTRIBUTOR' | 'CLIENT_ADMIN' | 'CLIENT_VIEWER';
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { auth } = useAuth();
  if (auth.kind === 'anonymous') return <>{fallback ?? null}</>;

  const allowed = (() => {
    if (auth.kind === 'agency') {
      if (role === 'SUPER_ADMIN') return auth.role === 'SUPER_ADMIN';
      if (role === 'ADMIN+') return auth.role === 'SUPER_ADMIN' || auth.role === 'ADMIN';
      if (role === 'ADMIN') return auth.role === 'SUPER_ADMIN' || auth.role === 'ADMIN';
      if (role === 'CONTRIBUTOR') return true;
    }
    if (auth.kind === 'contact') {
      if (role === 'CLIENT_ADMIN') return auth.role === 'CLIENT_ADMIN';
      if (role === 'CLIENT_VIEWER') return true;
    }
    return false;
  })();

  return allowed ? <>{children}</> : <>{fallback ?? null}</>;
}
